import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import {
  Truck,
  Lock,
  Copy,
  Settings,
  Webhook,
  Save,
  BadgeCheck,
  ShieldAlert,
  BookOpen,
  RefreshCcw,
} from "lucide-react";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * Plain-English walkthrough of turning on automatic delivery status
 * tracking (Delivered / Returned / Failed on the Orders tab) via Shippo's
 * webhook. Companion to CourierInfo.jsx and ShiprocketTrackingGuide.jsx,
 * same design system as StatusGuide.jsx / TabsGuide.jsx / CatalogGuide.jsx.
 */

const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]" },
};

const STEPS = [
  {
    icon: Copy,
    accent: "lilac",
    title: "Copy your Webhook URL",
    desc: "On CheckoutDM's Courier settings page, once Shippo is connected, you'll see a \"Delivery status tracking\" box with a Webhook URL in it. Tap Copy — this single URL already includes your store's unique token, so there's nothing else to copy.",
  },
  {
    icon: Settings,
    accent: "mint",
    title: "Open Shippo → Settings → API → Webhooks",
    desc: "Log in to your Shippo dashboard and go to Settings, then API, then the Webhooks tab.",
  },
  {
    icon: Webhook,
    accent: "peach",
    title: "Add a webhook",
    desc: "Click Add webhook, then set Event Type to Track Updated — this is what tells Shippo to notify CheckoutDM whenever a shipment's status changes.",
  },
  {
    icon: Save,
    accent: "lilac",
    title: "Match the Mode, paste the URL, and save",
    desc: "Set Mode to Test or Live — whichever matches the Shippo API key you saved on CheckoutDM's Courier page. Paste the Webhook URL you copied into the URL field, then save.",
  },
  {
    icon: BadgeCheck,
    accent: "mint",
    title: "You're done",
    desc: "From your next shipment onward, Shippo will notify CheckoutDM automatically the moment a status changes — Delivered, Returned, or Delivery failed will start showing on your Orders tab in place of a plain \"Shipped\".",
  },
];

export default function ShippoTrackingGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Shippo Delivery Status Tracking Guide",
    description: "How to connect Shippo's webhook to CheckoutDM so your Orders tab automatically shows Delivered, Returned, or Delivery failed.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/shippo-tracking-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Shippo Tracking Guide", item: "https://checkoutdm.com/shippo-tracking-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Shippo Delivery Tracking Guide | Checkout DM"
        description="How to connect Shippo's webhook to CheckoutDM so your Orders tab automatically shows Delivered, Returned, or Delivery failed."
        path="/shippo-tracking-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-3xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          Turn on automatic delivery tracking
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          Once this is on, your Orders tab automatically shows{" "}
          <strong>Delivered</strong>, <strong>Returned</strong>, or{" "}
          <strong>Delivery failed</strong> instead of a static "Shipped" — no more
          checking Shippo separately to know what actually happened to an order.
        </p>

        <div className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-ink/15 bg-ink/[0.03] px-3 py-1.5 text-[11px] font-semibold text-ink/60">
          <Lock size={11} /> Starter, Growth &amp; Pro
        </div>

        <section className="mt-8 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-start gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-lilac text-ink">
              <Truck size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">Setup</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                Four steps, one-time
              </h2>
            </div>
          </div>

          <ol className="mt-6 space-y-4">
            {STEPS.map((s, i) => {
              const a = ACCENTS[s.accent];
              return (
                <li key={s.title} className="flex gap-4 rounded-2xl bg-ink/[0.02] p-4">
                  <span className={`grid h-9 w-9 flex-none place-items-center rounded-xl ${a.chip} ${a.chipText} font-display text-sm font-black`}>
                    {i + 1}
                  </span>
                  <div>
                    <div className="flex items-center gap-2">
                      <s.icon size={15} className="text-ink/50" />
                      <p className="text-sm font-bold text-ink">{s.title}</p>
                    </div>
                    <p className="mt-1 text-[13px] leading-relaxed text-ink/70">{s.desc}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="mt-6 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-start gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-hotpink text-white">
              <ShieldAlert size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Good to know</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                A few things worth knowing
              </h2>
            </div>
          </div>
          <ul className="mt-4 space-y-2 text-sm text-ink/70">
            <li>• Your Webhook URL is unique to your store and already includes your token — don't share it, and don't paste it into a different Shippo or CheckoutDM account.</li>
            <li>• Make sure the Mode you choose in Shippo (Test or Live) matches whichever API key — Test or Live — you saved on CheckoutDM's Courier page. A mismatch means the webhook fires on shipments CheckoutDM isn't tracking.</li>
            <li>• This is available on Starter, Growth, and Pro plans — if you're on Free, your Orders tab keeps showing "Shipped" as it does today.</li>
            <li>• Turning this on is also what lets your return window calculate correctly from the real delivery date instead of an estimate.</li>
            <li>• You only need to do this once per Shippo account — it keeps working for every future order automatically.</li>
          </ul>
          <div className="mt-4 flex items-center gap-2 rounded-2xl bg-ink/[0.03] p-4 text-[13px] text-ink/60">
            <RefreshCcw size={14} className="flex-none text-ink/40" />
            If you ever disconnect and reconnect Shippo, you don't need to redo this step — your Webhook URL and token stay the same.
          </div>
        </section>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Still stuck?</p>
            <p className="mt-1 max-w-md text-sm text-ink/70">
              If something here still doesn't make sense, reach out and we'll add it to this guide for the next person too.
            </p>
          </div>
          <Link
            to="/courier"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
          >
            <BookOpen size={14} /> Back to Courier settings
          </Link>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
